'use client';

/**
 * The tabbed chat area: a strip, one MOUNTED pane per open chat, and the start
 * screen behind them.
 *
 * Every pane is absolutely positioned at full size and only the active one is
 * visible. Keeping the inactive ones LAID OUT (not `display: none`) is what
 * lets the virtualized message list hold its measurements and scroll position
 * across a tab switch.
 */

import React from 'react';
import { ChatTabStrip } from '../ChatTabStrip';
import { ChatTabPane } from '../ChatTabPane';
import { ChatStartScreen } from '../ui';
import type { ChatTab } from '../../hooks/useChatTabs';
import type { FilePart } from '../../lib/db/types';
import type { AttachedSkill } from '../skills/types';
import type { StartChatOptions } from '../ui/start-screen/useStartScreenComposer';
import type { BusinessInfo, Chat } from '../../types';

/** Stable identity so an inactive pane's `initialFiles` prop never changes. */
const EMPTY_FILES: FilePart[] = [];

export interface ChatTabbedAreaProps {
  visibleTabs: ChatTab[];
  selectedChatId: string | null;
  apiBaseUrl: string;
  locale: string;
  isLoading: boolean;
  initialQuery: string;
  initialFiles: FilePart[];
  initialSkill: AttachedSkill | null;
  userContext: BusinessInfo | null;
  onSelectChat: (chatId: string | null) => void;
  onCloseTab: (chatId: string) => void;
  onMessageSent: () => void;
  onShare: (chatId: string) => void;
  onArchive: (chatId: string, chat: Chat) => void;
  onDelete: (chatId?: string) => void;
  onTitleResolved: (chatId: string, title: string) => void;
  onStartChat: (query: string, options: StartChatOptions) => void;
  onBackToList: () => void;
  botAvatarUrl?: string;
  userInitials?: string;
  userAvatarUrl?: string;
  meridianLogoUrl: string;
  mcpConnectionsHref?: string;
  compactStartScreen: boolean;
  showSlackCallout: boolean;
  slackCalloutMentionsSearch: boolean;
  showModelPicker: boolean;
}

export function ChatTabbedArea({
  visibleTabs,
  selectedChatId,
  apiBaseUrl,
  locale,
  isLoading,
  initialQuery,
  initialFiles,
  initialSkill,
  userContext,
  onSelectChat,
  onCloseTab,
  onMessageSent,
  onShare,
  onArchive,
  onDelete,
  onTitleResolved,
  onStartChat,
  onBackToList,
  botAvatarUrl,
  userInitials,
  userAvatarUrl,
  meridianLogoUrl,
  mcpConnectionsHref,
  compactStartScreen,
  showSlackCallout,
  slackCalloutMentionsSearch,
  showModelPicker,
}: ChatTabbedAreaProps) {
  return (
    <>
      <ChatTabStrip
        tabs={visibleTabs}
        activeChatId={selectedChatId}
        onActivate={onSelectChat}
        onClose={onCloseTab}
        // "+" goes to the start screen rather than creating an empty chat row:
        // the chat is created when the first message is sent.
        onNewChat={() => onSelectChat(null)}
        isCreatingChat={isLoading}
      />
      <div className="relative flex-1 min-h-0">
        {visibleTabs.map((tab) => (
          <ChatTabPane
            key={tab.id}
            chatId={tab.id}
            isActive={tab.id === selectedChatId}
            apiBaseUrl={apiBaseUrl}
            locale={locale}
            // First-message seeds belong to the chat that was just started —
            // which is always the active one.
            initialQuery={tab.id === selectedChatId ? initialQuery : ''}
            initialFiles={tab.id === selectedChatId ? initialFiles : EMPTY_FILES}
            initialSkill={tab.id === selectedChatId ? initialSkill : null}
            onMessageSent={onMessageSent}
            onShare={onShare}
            onArchive={onArchive}
            onDelete={onDelete}
            onTitleResolved={onTitleResolved}
            botAvatarUrl={botAvatarUrl}
            userInitials={userInitials}
            userAvatarUrl={userAvatarUrl}
            meridianLogoUrl={meridianLogoUrl}
            mcpConnectionsHref={mcpConnectionsHref}
            onBackToList={onBackToList}
          />
        ))}
        <div
          className={`absolute inset-0 flex flex-col ${
            selectedChatId ? 'invisible pointer-events-none' : ''
          }`}
          inert={Boolean(selectedChatId)}
          aria-hidden={Boolean(selectedChatId)}
        >
          <ChatStartScreen
            userContext={userContext}
            locale={locale}
            isLoading={isLoading}
            onStartChat={onStartChat}
            compact={compactStartScreen}
            meridianLogoUrl={meridianLogoUrl}
            showSlackCallout={showSlackCallout}
            slackCalloutMentionsSearch={slackCalloutMentionsSearch}
            mcpConnectionsHref={mcpConnectionsHref}
            apiBaseUrl={apiBaseUrl}
            showModelPicker={showModelPicker}
          />
        </div>
      </div>
    </>
  );
}
